/**
 * Daily Expenses Page
 * 
 * Management of day-to-day expenses with frequency-based planning
 * and analytical summary for spending suggestions.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { DailyExpenseTabsList } from '@/components/financial/DailyExpenseTabs';
import { DueEmptyState } from '@/components/financial/DueFilterTabs';
import {
  dailyExpenseTabCounts,
  filterDailyExpensesByTab,
  isDailyExpenseExceeded,
  type DailyExpenseTab,
} from '@/lib/daily-expense-tabs';
import { differenceInCalendarDays, format, getDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Plus,
  Loader2,
  ShoppingCart,
  Coffee,
  Car,
  AlertCircle,
  MoreVertical,
  Check,
  Edit,
  Trash2,
  RefreshCw,
  Calculator,
  Search,
  Copy,
  ArrowUpDown,
  CalendarDays,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Combobox } from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';
import { DateInput } from '@/components/ui/date-input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { CurrencyDisplay } from '@/components/financial/CurrencyDisplay';
import { FinancialSummaryCards } from '@/components/financial/FinancialSummaryCards';
import { PeriodNavigator, BulkSelectionSummary } from '@/components/financial';
import { FeatureGuard } from '@/hooks/use-plan-features';
import { FeatureLockButton } from '@/components/subscription/FeatureBlockedPage';
import {
  parseDateStringToLocalDate,
  formatDatePtBR,
  getBudgetPeriodPermissions,
  isDateWithinPeriod,
  getDefaultRealizationDate,
} from '@/lib/date';
import { normalizeSearchText } from '@/lib/text';
import { toast } from '@/hooks/use-toast';

import {
  useCurrentBudgetPeriod,
  useBudgetPeriodNavigation,
  useNavigatePeriod,
  useDailyExpenses,
  useDailyExpensesByPeriod,
  useCreateDailyExpense,
  useUpdateDailyExpense,
  useDeleteDailyExpense,
  useRealizeDailyExpense,
  useUpdateDailyExpenseRealization,
  useDeleteDailyExpenseRealization,
  useCopyDailyExpensesFromPrevious,
  useDailyExpenseCopySourcePeriods,
  useFinancialList,
  useAgents,
  useFinancialSettings,
} from '@/hooks/use-financial';
import {
  FIRST_ACCESS_TUTORIAL_DEMO,
  FIRST_ACCESS_TUTORIAL_STEPS,
  getTutorialDateInBudgetPeriod,
  markTutorialKnownItemIds,
  findTutorialCreatedItem,
  requestTutorialAdvance,
  useActiveOnboardingStep,
} from '@/components/onboarding/tutorial-runtime';

const TUTORIAL_SCOPE = 'daily-expenses';

// Realce do botão de criação quando o formulário está completo.
const CREATE_READY_BUTTON_CLASS =
  'bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:ring-emerald-600';

import type {
  DailyExpense,
  DailyExpenseCreate,
  DailyExpenseRealization,
  DailyExpenseUpdate,
} from '@/types/financial';

// Category icons mapping
const categoryIcons: Record<string, typeof ShoppingCart> = {
  'Alimentação': Coffee,
  'Transporte': Car,
  'Compras': ShoppingCart,
};

// Aviso das abas Excedidas / Sem lançamento quando não há nenhuma despesa nelas.
const ABA_VAZIA: Record<Exclude<DailyExpenseTab, 'all'>, { title: string; message: string }> = {
  exceeded: {
    title: 'Nenhuma despesa excedida!',
    message: 'Todas as despesas estão dentro do planejado 🎉',
  },
  unlaunched: {
    title: 'Nenhuma despesa sem lançamento!',
    message: 'Todas as despesas já têm lançamentos.',
  },
};

export default function DailyExpensesPage() {
  const appliedTutorialStepRef = useRef<string | null>(null);
  const { data: currentPeriod, isLoading: isLoadingPeriod } = useCurrentBudgetPeriod();
  const { data: navigation } = useBudgetPeriodNavigation();
  const { mutate: navigatePeriod, isPending: isNavigating } = useNavigatePeriod();
  const { data: expenses, isLoading: isLoadingExpenses } = useDailyExpenses(currentPeriod?.id);
  const { data: expenseNamesList } = useFinancialList('daily_expense_names');
  const { data: categoriesList } = useFinancialList('expense_categories');
  const { data: paymentMethodsList } = useFinancialList('payment_methods');
  const { data: agents } = useAgents();
  const expenseAgents = useMemo(() => agents || [], [agents]);
  const payerAgents = useMemo(() => {
    const allAgents = agents || [];
    const preferredAgents = allAgents.filter(
      (agent) => agent.is_default_payer || agent.is_default_agent
    );
    return preferredAgents.length > 0 ? preferredAgents : allAgents;
  }, [agents]);
  const agentNameById = useMemo(
    () =>
      new Map((agents || []).map((agent) => [agent.id, agent.name])),
    [agents]
  );
  const { data: financialSettings } = useFinancialSettings();
  const activeTutorialStep = useActiveOnboardingStep();

  const { mutate: createExpense, isPending: isCreating } = useCreateDailyExpense();
  const { mutate: updateExpense, isPending: isUpdating } = useUpdateDailyExpense();
  const { mutate: deleteExpense, mutateAsync: deleteExpenseAsync, isPending: isDeleting } =
    useDeleteDailyExpense();
  const { mutate: realizeExpense, isPending: isRealizing } = useRealizeDailyExpense();
  const { mutate: updateRealization, isPending: isUpdatingRealization } = useUpdateDailyExpenseRealization();
  const { mutate: deleteRealization, isPending: isDeletingRealization } = useDeleteDailyExpenseRealization();
  const { mutate: copyFromPrevious, isPending: isCopying } = useCopyDailyExpensesFromPrevious();

  const periodPermissions = getBudgetPeriodPermissions({
    startDate: currentPeriod?.start_date,
    endDate: currentPeriod?.end_date,
    paymentDay: financialSettings?.default_payment_day ?? currentPeriod?.payment_day,
  });
  const canPlanInPeriod = periodPermissions.canPlan;
  const canRealizeInPeriod = periodPermissions.canRealize;

  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isRealizeDialogOpen, setIsRealizeDialogOpen] = useState(false);
  const [isAnalyticsDialogOpen, setIsAnalyticsDialogOpen] = useState(false);
  const [analyticsExpense, setAnalyticsExpense] = useState<DailyExpense | null>(null);
  const [isEditRealizationDialogOpen, setIsEditRealizationDialogOpen] = useState(false);
  const [isDeleteRealizationDialogOpen, setIsDeleteRealizationDialogOpen] = useState(false);
  const [selectedRealization, setSelectedRealization] = useState<DailyExpenseRealization | null>(null);
  // Sem setter: nada nunca chamou o toggle, entao o collapsible de Analytics
  // ja era permanentemente fechado. Mantido o valor para nao mudar o render.
  const [isAnalyticsOpen] = useState<Record<string, boolean>>({});
  const [selectedExpense, setSelectedExpense] = useState<DailyExpense | null>(null);
  const [expenseToEdit, setExpenseToEdit] = useState<DailyExpense | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Aba ativa (Todas / Excedidas / Sem lançamento); a busca vale por cima.
  const [activeTab, setActiveTab] = useState<DailyExpenseTab>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<string[]>([]);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isCopyPreviousDialogOpen, setIsCopyPreviousDialogOpen] = useState(false);
  const [isPeriodCalendarDialogOpen, setIsPeriodCalendarDialogOpen] = useState(false);
  const [copyDataScope, setCopyDataScope] = useState<'planned' | 'realized'>('planned');
  const [copySourcePeriodId, setCopySourcePeriodId] = useState('');
  const [selectedCopyIds, setSelectedCopyIds] = useState<string[]>([]);

  const { data: copySourcePeriods, isLoading: isCopySourcesLoading } = useDailyExpenseCopySourcePeriods(
    currentPeriod?.id,
    copyDataScope,
    isCopyPreviousDialogOpen
  );
  const { data: copySourceExpenses, isLoading: isCopySourceExpensesLoading } = useDailyExpensesByPeriod(
    copySourcePeriodId,
    isCopyPreviousDialogOpen && !!copySourcePeriodId
  );

  // Form states
  const [createFormData, setCreateFormData] = useState({
    name: '',
    frequency: '',
    average_value: '',
    category: '',
    notes: '',
    payer_agent_id: '',
  });
  const isCreateDialogInEditMode = !!expenseToEdit;
  const isSavingPlannedExpense = isCreateDialogInEditMode ? isUpdating : isCreating;

  const [realizeFormData, setRealizeFormData] = useState({
    amount: 0,
    realization_date: format(new Date(), 'yyyy-MM-dd'),
    payment_method: '',
    notes: '',
    agent_id: '',
  });

  const [editRealizationFormData, setEditRealizationFormData] = useState({
    amount: 0,
    realization_date: format(new Date(), 'yyyy-MM-dd'),
    payment_method: '',
    notes: '',
    agent_id: '',
  });

  // Calculations
  const totalPlanned = expenses?.reduce((sum, e) => sum + Number(e.planned_total || 0), 0) || 0;
  const totalRealized = expenses?.reduce((sum, e) => sum + Number(e.realized_total || 0), 0) || 0;
  const remainingBudget = totalPlanned - totalRealized;

  const defaultPayerAgentId =
    payerAgents.find((agent) => agent.is_default_agent)?.id ||
    payerAgents.find((agent) => agent.is_default_payer)?.id ||
    payerAgents[0]?.id ||
    expenseAgents.find((agent) => agent.is_default_agent)?.id ||
    expenseAgents[0]?.id ||
    '';

  const defaultExpenseAgentId =
    expenseAgents.find((agent) => agent.is_default_agent)?.id ||
    expenseAgents[0]?.id ||
    defaultPayerAgentId;
  const tutorialDateInPeriod = getTutorialDateInBudgetPeriod(currentPeriod);
  const tutorialCategory = categoriesList?.items?.[0]?.name || '';
  const tutorialPaymentMethod = paymentMethodsList?.items?.[0]?.name || '';
  const tutorialExpense = useMemo(
    () => findTutorialCreatedItem(TUTORIAL_SCOPE, expenses),
    [expenses]
  );

  const periodCalendarData = useMemo(() => {
    const start = currentPeriod?.start_date ? parseDateStringToLocalDate(currentPeriod.start_date) : null;
    const end = currentPeriod?.end_date ? parseDateStringToLocalDate(currentPeriod.end_date) : null;

    if (!start || !end || start > end) return null;

    const weekdayLabels = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    const totalDays = differenceInCalendarDays(end, start) + 1;
    const fullWeeks = Math.floor(totalDays / 7);
    const remainder = totalDays % 7;
    const startWeekday = getDay(start);
    const weekdayCounts = weekdayLabels.map((label, index) => ({
      label,
      count: fullWeeks + (((index - startWeekday + 7) % 7) < remainder ? 1 : 0),
    }));

    return {
      start,
      end,
      totalDays,
      weekdayCounts,
    };
  }, [currentPeriod?.start_date, currentPeriod?.end_date]);

  /** Formulário completo: habilita o envio e recebe o realce verde. */
  const isCreateFormComplete =
    Boolean(createFormData.name) &&
    Boolean(createFormData.frequency) &&
    Boolean(createFormData.average_value) &&
    Boolean(createFormData.category) &&
    Boolean(createFormData.payer_agent_id);

  useEffect(() => {
    if (!isCreateDialogOpen) return;
    setCreateFormData((prev) => ({
      ...prev,
      payer_agent_id: prev.payer_agent_id || defaultExpenseAgentId,
    }));
  }, [isCreateDialogOpen, defaultExpenseAgentId]);

  useEffect(() => {
    const stepId = FIRST_ACCESS_TUTORIAL_STEPS.dailySave;

    if (activeTutorialStep !== stepId) {
      if (appliedTutorialStepRef.current === stepId) {
        appliedTutorialStepRef.current = null;
      }
      return;
    }

    if (!canPlanInPeriod || appliedTutorialStepRef.current === stepId) return;

    appliedTutorialStepRef.current = stepId;
    // Fotografa o que já existe: o registro que sobrar depois é o lançamento
    // real que o usuário acabou de criar no tutorial.
    markTutorialKnownItemIds(TUTORIAL_SCOPE, (expenses || []).map((expense) => expense.id));
    setExpenseToEdit(null);
    setCreateFormData({
      // Nome vazio de propósito: o usuário escolhe, na listagem existente,
      // uma despesa verdadeira dele.
      name: '',
      frequency: String(FIRST_ACCESS_TUTORIAL_DEMO.dailyExpense.frequency),
      average_value: String(FIRST_ACCESS_TUTORIAL_DEMO.dailyExpense.averageValue),
      category: tutorialCategory,
      notes: '',
      payer_agent_id: defaultExpenseAgentId || '',
    });
    setIsCreateDialogOpen(true);
  }, [
    activeTutorialStep,
    canPlanInPeriod,
    defaultExpenseAgentId,
    tutorialCategory,
    expenses,
  ]);

  useEffect(() => {
    const stepId = FIRST_ACCESS_TUTORIAL_STEPS.dailyConfirm;

    if (activeTutorialStep !== stepId) {
      if (appliedTutorialStepRef.current === stepId) {
        appliedTutorialStepRef.current = null;
      }
      return;
    }

    if (!tutorialExpense || appliedTutorialStepRef.current === stepId) return;

    appliedTutorialStepRef.current = stepId;
    setSelectedExpense(tutorialExpense);
    setRealizeFormData({
      amount: Number(tutorialExpense.average_value || FIRST_ACCESS_TUTORIAL_DEMO.dailyExpense.averageValue),
      realization_date: tutorialDateInPeriod,
      payment_method: tutorialExpense.payment_method || tutorialPaymentMethod,
      notes: '',
      agent_id: tutorialExpense.payer_agent_id || defaultPayerAgentId || '',
    });
    setIsRealizeDialogOpen(true);
  }, [
    activeTutorialStep,
    defaultPayerAgentId,
    tutorialDateInPeriod,
    tutorialExpense,
    tutorialPaymentMethod,
  ]);

  useEffect(() => {
    if (!isCopyPreviousDialogOpen) {
      setCopySourcePeriodId('');
      setCopyDataScope('planned');
      setSelectedCopyIds([]);
    }
  }, [isCopyPreviousDialogOpen]);

  useEffect(() => {
    if (!isCopyPreviousDialogOpen) return;
    setCopySourcePeriodId('');
    setSelectedCopyIds([]);
  }, [copyDataScope, isCopyPreviousDialogOpen]);

  useEffect(() => {
    if (!isCopyPreviousDialogOpen) return;
    setSelectedCopyIds([]);
  }, [copySourcePeriodId, isCopyPreviousDialogOpen]);

  // Days remaining in period

  const copyCandidates = useMemo(() => {
    if (!copySourceExpenses) return [];
    if (copyDataScope === 'realized') {
      return copySourceExpenses.filter((expense) => (expense.realized_count || 0) > 0);
    }
    return copySourceExpenses;
  }, [copySourceExpenses, copyDataScope]);

  const selectedCopyCount = selectedCopyIds.length;
  const allCopySelected = copyCandidates.length > 0 && selectedCopyCount === copyCandidates.length;
  const someCopySelected = selectedCopyCount > 0 && !allCopySelected;

  const formatPeriodLabel = (startDate?: string, endDate?: string) => {
    const start = startDate ? parseDateStringToLocalDate(startDate) : null;
    const end = endDate ? parseDateStringToLocalDate(endDate) : null;
    const startLabel = start ? start.toLocaleDateString('pt-BR') : startDate || '';
    const endLabel = end ? end.toLocaleDateString('pt-BR') : endDate || '';
    return `${startLabel} - ${endLabel}`.trim();
  };

  const tabCounts = dailyExpenseTabCounts(expenses);
  const tabExpenses = filterDailyExpensesByTab(expenses || [], activeTab);
  // Avisos de lista vazia: aba sem despesas, busca sem resultado dentro da aba
  // ou nenhuma despesa cadastrada no período.
  const abaVazia = activeTab !== 'all' && tabExpenses.length === 0;
  const semDespesas = activeTab === 'all' && tabExpenses.length === 0;

  // Filter expenses (mesma lógica base das telas financeiras)
  const filteredExpenses = tabExpenses.filter((expense) => {
    const matchesSearch = normalizeSearchText(expense.name).includes(normalizeSearchText(searchTerm));
    return matchesSearch;
  });

  // Sort expenses
  const sortedExpenses = [...filteredExpenses].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' });
        break;
      case 'value':
        comparison = Number(a.average_value) - Number(b.average_value);
        break;
      case 'planned':
        comparison = Number(a.planned_total) - Number(b.planned_total);
        break;
      case 'remaining':
        comparison =
          (Number(a.planned_total || 0) - Number(a.realized_total || 0)) -
          (Number(b.planned_total || 0) - Number(b.realized_total || 0));
        break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  const semResultadoNaBusca = tabExpenses.length > 0 && sortedExpenses.length === 0;
  const visibleExpenseIds = sortedExpenses.map((expense) => expense.id);
  const selectedVisibleExpenseIds = selectedExpenseIds.filter((id) => visibleExpenseIds.includes(id));
  const selectedExpenseCount = selectedVisibleExpenseIds.length;
  const allExpensesSelected =
    visibleExpenseIds.length > 0 && selectedExpenseCount === visibleExpenseIds.length;
  const someExpensesSelected = selectedExpenseCount > 0 && !allExpensesSelected;
  const selectedExpenses = sortedExpenses.filter((expense) =>
    selectedVisibleExpenseIds.includes(expense.id)
  );
  const selectedPlannedTotal = selectedExpenses.reduce(
    (sum, expense) => sum + Number(expense.planned_total || 0),
    0
  );
  const selectedRealizedTotal = selectedExpenses.reduce(
    (sum, expense) => sum + Number(expense.realized_total || 0),
    0
  );

  // Handlers
  const handleCreateExpense = () => {
    if (
      !createFormData.name ||
      !createFormData.frequency ||
      !createFormData.average_value ||
      !createFormData.category ||
      !createFormData.payer_agent_id ||
      !currentPeriod?.id
    )
      return;

    const data: DailyExpenseCreate = {
      name: createFormData.name,
      frequency: Number.parseInt(createFormData.frequency),
      average_value: Number.parseFloat(createFormData.average_value),
      category: createFormData.category,
      payer_agent_id: createFormData.payer_agent_id,
      notes: createFormData.notes || undefined,
    };

    createExpense({ data, periodId: currentPeriod.id }, {
      onSuccess: () => {
        requestTutorialAdvance(FIRST_ACCESS_TUTORIAL_STEPS.dailySave);
        setIsCreateDialogOpen(false);
        setExpenseToEdit(null);
        setCreateFormData({
          name: '',
          frequency: '',
          average_value: '',
          category: '',
          notes: '',
          payer_agent_id: '',
        });
      },
    });
  };

  const handleOpenCreateDialog = () => {
    setExpenseToEdit(null);
    setCreateFormData({
      name: '',
      frequency: '',
      average_value: '',
      category: '',
      notes: '',
      payer_agent_id: defaultExpenseAgentId || '',
    });
    setIsCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setExpenseToEdit(null);
  };

  const handleSavePlannedExpense = () => {
    if (
      !createFormData.name ||
      !createFormData.frequency ||
      !createFormData.average_value ||
      !createFormData.category ||
      !createFormData.payer_agent_id
    ) {
      return;
    }

    const data: DailyExpenseUpdate = {
      name: createFormData.name,
      frequency: Number.parseInt(createFormData.frequency, 10),
      average_value: Number.parseFloat(createFormData.average_value),
      category: createFormData.category || undefined,
      payer_agent_id: createFormData.payer_agent_id || undefined,
      notes: createFormData.notes || undefined,
    };

    if (isCreateDialogInEditMode && expenseToEdit) {
      updateExpense(
        { id: expenseToEdit.id, data },
        {
          onSuccess: () => {
            setIsCreateDialogOpen(false);
            setExpenseToEdit(null);
            setCreateFormData({
              name: '',
              frequency: '',
              average_value: '',
              category: '',
              notes: '',
              payer_agent_id: '',
            });
          },
        }
      );
      return;
    }

    handleCreateExpense();
  };

  const handleOpenEditDialog = (expense: DailyExpense) => {
    setExpenseToEdit(expense);
    setCreateFormData({
      name: expense.name,
      frequency: String(expense.frequency || ''),
      average_value: String(expense.average_value || ''),
      category: expense.category || '',
      payer_agent_id: expense.payer_agent_id || defaultExpenseAgentId || '',
      notes: expense.notes || '',
    });
    setIsCreateDialogOpen(true);
  };

  const handleToggleCopySelection = (expenseId: string, checked: boolean) => {
    setSelectedCopyIds((prev) => {
      if (checked) {
        return prev.includes(expenseId) ? prev : [...prev, expenseId];
      }
      return prev.filter((id) => id !== expenseId);
    });
  };

  const handleToggleCopyAll = (checked: boolean) => {
    if (checked) {
      setSelectedCopyIds(copyCandidates.map((expense) => expense.id));
      return;
    }
    setSelectedCopyIds([]);
  };

  const handleCopySelectedExpenses = () => {
    if (!currentPeriod?.id || !copySourcePeriodId || selectedCopyIds.length === 0) return;

    copyFromPrevious(
      {
        target_period_id: currentPeriod.id,
        source_period_id: copySourcePeriodId,
        copy_data: copyDataScope,
        expense_ids: selectedCopyIds,
      },
      {
        onSuccess: () => {
          setIsCreateDialogOpen(false);
          setIsCopyPreviousDialogOpen(false);
          setSelectedCopyIds([]);
          setCopySourcePeriodId('');
        },
      }
    );
  };

  const handleOpenRealizeDialog = (expense: DailyExpense) => {
    setSelectedExpense(expense);
    setRealizeFormData({
      amount: 0,
      realization_date: getDefaultRealizationDate(
        currentPeriod?.start_date,
        currentPeriod?.end_date
      ),
      // Sugestão: a forma do lançamento mais recente desta despesa.
      payment_method: expense.last_payment_method || '',
      notes: '',
      agent_id: defaultPayerAgentId,
    });
    setIsRealizeDialogOpen(true);
  };

  const handleRealizeExpense = () => {
    if (!selectedExpense || !realizeFormData.amount || !realizeFormData.payment_method) return;

    if (
      !isDateWithinPeriod(
        realizeFormData.realization_date,
        currentPeriod?.start_date,
        currentPeriod?.end_date
      )
    ) {
      toast({
        title: 'Data fora do período orçamentário',
        description: `A data de realização deve estar dentro do período do lançamento (${formatDatePtBR(
          currentPeriod?.start_date
        )} a ${formatDatePtBR(currentPeriod?.end_date)}).`,
        variant: 'destructive',
      });
      return;
    }

    realizeExpense(
      {
        id: selectedExpense.id,
        data: {
          amount: realizeFormData.amount,
          realization_date: realizeFormData.realization_date,
          payment_method: realizeFormData.payment_method,
          notes: realizeFormData.notes,
          agent_id: realizeFormData.agent_id || undefined,
        },
      },
      {
        onSuccess: (updatedExpense) => {
          requestTutorialAdvance(FIRST_ACCESS_TUTORIAL_STEPS.dailyConfirm);
          setIsRealizeDialogOpen(false);
          setSelectedExpense(null);
          if (activeTutorialStep !== FIRST_ACCESS_TUTORIAL_STEPS.dailyConfirm) {
            setAnalyticsExpense(updatedExpense as DailyExpense);
            setIsAnalyticsDialogOpen(true);
          }
        },
      }
    );
  };

  const handleOpenAnalyticsDialog = (expense: DailyExpense) => {
    setAnalyticsExpense(expense);
    setIsAnalyticsDialogOpen(true);
  };

  const handleAddRealizationFromAnalytics = () => {
    if (!analyticsExpense) return;
    setIsAnalyticsDialogOpen(false);
    handleOpenRealizeDialog(analyticsExpense);
  };

  const handleOpenEditRealizationDialog = (expense: DailyExpense, realization: DailyExpenseRealization) => {
    setAnalyticsExpense(expense);
    setSelectedRealization(realization);
    setEditRealizationFormData({
      amount: Number(realization.amount) || 0,
      realization_date: realization.realization_date,
      payment_method: realization.payment_method || '',
      notes: realization.notes || '',
      agent_id: realization.agent_id || defaultPayerAgentId || expense.payer_agent_id || '',
    });
    setIsEditRealizationDialogOpen(true);
  };

  const handleOpenDeleteRealizationDialog = (expense: DailyExpense, realization: DailyExpenseRealization) => {
    setAnalyticsExpense(expense);
    setSelectedRealization(realization);
    setIsDeleteRealizationDialogOpen(true);
  };

  const handleUpdateRealization = () => {
    if (!analyticsExpense || !selectedRealization) return;
    if (!editRealizationFormData.amount || !editRealizationFormData.payment_method) return;

    if (
      !isDateWithinPeriod(
        editRealizationFormData.realization_date,
        currentPeriod?.start_date,
        currentPeriod?.end_date
      )
    ) {
      toast({
        title: 'Data fora do período orçamentário',
        description: `A data de realização deve estar dentro do período do lançamento (${formatDatePtBR(
          currentPeriod?.start_date
        )} a ${formatDatePtBR(currentPeriod?.end_date)}).`,
        variant: 'destructive',
      });
      return;
    }

    updateRealization(
      {
        expenseId: analyticsExpense.id,
        realizationId: selectedRealization.id,
        data: {
          amount: editRealizationFormData.amount,
          realization_date: editRealizationFormData.realization_date,
          payment_method: editRealizationFormData.payment_method,
          notes: editRealizationFormData.notes || undefined,
          agent_id: editRealizationFormData.agent_id || undefined,
        },
      },
      {
        onSuccess: (updatedExpense) => {
          setAnalyticsExpense(updatedExpense as DailyExpense);
          setIsEditRealizationDialogOpen(false);
          setSelectedRealization(null);
        },
      }
    );
  };

  const handleDeleteRealization = () => {
    if (!analyticsExpense || !selectedRealization) return;

    deleteRealization(
      { expenseId: analyticsExpense.id, realizationId: selectedRealization.id },
      {
        onSuccess: (updatedExpense) => {
          setAnalyticsExpense(updatedExpense as DailyExpense);
          setIsDeleteRealizationDialogOpen(false);
          setSelectedRealization(null);
        },
      }
    );
  };

  const plannedVsRealized = useMemo(() => {
    if (!analyticsExpense) return null;

    const plannedAverage = Number(analyticsExpense.average_value || 0);
    const plannedFrequency = Number(analyticsExpense.frequency || 0);
    const plannedTotal = Number(analyticsExpense.planned_total || 0);

    const realizedCount = Number(analyticsExpense.realized_count || 0);
    const realizedTotal = Number(analyticsExpense.realized_total || 0);
    const realizedAverage = realizedCount > 0 ? realizedTotal / realizedCount : 0;

    const saldo = plannedTotal - realizedTotal;

    const currencyFmt = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const saldoDisplay = `${saldo <= 0 ? '-' : ''}${currencyFmt.format(Math.abs(saldo))}`;

    return {
      plannedAverage,
      plannedFrequency,
      plannedTotal,
      realizedAverage,
      realizedCount,
      realizedTotal,
      saldo,
      saldoDisplay,
    };
  }, [analyticsExpense]);

  const handleDeleteExpense = (expense: DailyExpense) => {
    if (confirm(`Tem certeza que deseja excluir "${expense.name}"?`)) {
      deleteExpense(expense.id);
    }
  };

  const handleToggleExpenseSelection = (expenseId: string, checked: boolean) => {
    setSelectedExpenseIds((prev) => {
      if (checked) {
        return prev.includes(expenseId) ? prev : [...prev, expenseId];
      }
      return prev.filter((id) => id !== expenseId);
    });
  };

  const handleToggleAllExpenses = (checked: boolean) => {
    if (checked) {
      setSelectedExpenseIds(visibleExpenseIds);
      return;
    }
    setSelectedExpenseIds([]);
  };

  const handleBulkDeleteExpenses = async () => {
    if (selectedVisibleExpenseIds.length === 0) return;

    const idsToDelete = [...selectedVisibleExpenseIds];
    setIsBulkDeleting(true);

    const results = await Promise.allSettled(idsToDelete.map((id) => deleteExpenseAsync(id)));
    const failedIds = idsToDelete.filter((_, index) => results[index]?.status === 'rejected');

    setIsBulkDeleting(false);

    if (failedIds.length === 0) {
      setSelectedExpenseIds([]);
      setIsBulkDeleteDialogOpen(false);
      return;
    }

    setSelectedExpenseIds(failedIds);
  };


  useEffect(() => {
    setSelectedExpenseIds((prev) => {
      const next = prev.filter((id) => visibleExpenseIds.includes(id));
      return next.length === prev.length ? prev : next;
    });
  }, [visibleExpenseIds]);

  // Calculate suggestion for each expense
  // A sugestão é baseada no SALDO restante, não apenas na frequência
  const getSuggestion = (expense: DailyExpense | null) => {
    if (!expense) return 0;
    const remaining = Number(expense.planned_total || 0) - Number(expense.realized_total || 0);
    
    // Se não há mais saldo, retorna 0
    if (remaining <= 0) return 0;
    
    const remainingOccurrences = expense.frequency - (expense.realized_count || 0);
    
    // Se a frequência foi atingida mas ainda há saldo, retorna o saldo restante
    // Isso permite que o usuário veja quanto ainda pode gastar
    if (remainingOccurrences <= 0) return remaining;
    
    // Caso normal: distribui o saldo restante pelas ocorrências restantes
    return remaining / remainingOccurrences;
  };

  const getStatusColor = (expense: DailyExpense | null) => {
    if (!expense) return 'outline';
    const realized = Number(expense.realized_total || 0);
    const planned = Number(expense.planned_total || 0);
    if (isDailyExpenseExceeded(expense)) return 'destructive';
    if (realized === planned) return 'default';
    const rate = realized / planned;
    if (rate > 0.8) return 'secondary';
    return 'outline';
  };

  if (isLoadingPeriod || isLoadingExpenses) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6 max-w-full">
      {/* Page Header */}
      <div className="flex flex-col">
        <div className="text-left">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
            Despesas do Dia a Dia
          </h1>
          <p className="text-sm text-muted-foreground">
            Planeje e acompanhe o seu dia a dia financeiro e viva com <span className="font-semibold">prazer</span> e tranquilidade
          </p>
        </div>
        <div className="mt-8 flex items-center gap-3">
          <PeriodNavigator
            currentPeriod={currentPeriod}
            periods={navigation?.periods || []}
            isLoading={isNavigating}
            onNavigate={navigatePeriod}
            variant="boxed"
          />
        </div>
      </div>

      {/* Financial Summary Cards - sempre o 1º bloco de KPIs e fixo ao rolar */}
      <FinancialSummaryCards periodId={currentPeriod?.id} />

      {/* Abas Todas / Excedidas / Sem lançamento (mesmo padrão de Receitas e Despesas Mensais) */}
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as DailyExpenseTab)}
        className="space-y-4 sm:space-y-6"
      >
        <DailyExpenseTabsList counts={tabCounts} />

      {/* Filters (padrão: igual Despesas Mensais) */}
      <Card id="sheet">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
            {/* Primary actions row */}
            <div className="flex gap-2 sm:gap-4">
              <Button
                onClick={handleOpenCreateDialog}
                className="gap-2 flex-1 sm:flex-none"
                disabled={!canPlanInPeriod}
                data-tour-id="create-daily-expense"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Nova Despesa</span>
                <span className="sm:hidden">Nova</span>
              </Button>
            </div>
            
            {/* Search */}
            <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar despesas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchTerm.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setSearchTerm('')}
                  aria-label="Limpar busca de despesas do dia a dia"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            
            {/* Filters row */}
            <div className="flex flex-wrap gap-2 sm:gap-4">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-[170px]">
                  <SelectValue placeholder="Ordenar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Nome (A-Z)</SelectItem>
                  <SelectItem value="value">Valor Médio</SelectItem>
                  <SelectItem value="planned">Total Planejado</SelectItem>
                  <SelectItem value="remaining">Saldo Restante</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                className="h-10 w-10 shrink-0"
                title={sortDirection === 'asc' ? 'Ordenação crescente' : 'Ordenação decrescente'}
              >
                <ArrowUpDown className={`h-4 w-4 transition-transform ${sortDirection === 'desc' ? 'rotate-180' : ''}`} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {canPlanInPeriod && sortedExpenses.length > 0 && (
        <Card>
          <CardContent className="px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <Checkbox
                id="select-all-daily-expenses"
                checked={allExpensesSelected ? true : someExpensesSelected ? 'indeterminate' : false}
                onCheckedChange={(checked) => handleToggleAllExpenses(checked === true)}
                aria-label="Selecionar todas as despesas do dia a dia"
              />
              <Label htmlFor="select-all-daily-expenses" className="cursor-pointer">
                Selecionar Todos
              </Label>
            </div>
            {selectedExpenseCount > 0 && (
              <div className="flex flex-col gap-3 w-full sm:w-auto sm:flex-row sm:items-center sm:gap-4">
                <BulkSelectionSummary
                  plannedLabel="Planejado"
                  plannedTotal={selectedPlannedTotal}
                  realizedLabel="Realizado"
                  realizedTotal={selectedRealizedTotal}
                />
                <Button
                  variant="destructive"
                  size="sm"
                  className="gap-2 w-full sm:w-auto"
                  onClick={() => setIsBulkDeleteDialogOpen(true)}
                  disabled={isBulkDeleting || isDeleting}
                >
                  <Trash2 className="h-4 w-4" />
                  Excluir ({selectedExpenseCount})
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Alert if over budget */}
      {remainingBudget < 0 && (
        <Card className="border-rose-200 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/20">
          <CardContent className="flex items-center gap-4 py-4">
            <AlertCircle className="h-6 w-6 text-rose-600" />
            <div>
              <h3 className="font-medium text-rose-800 dark:text-rose-200">
                Orçamento ultrapassado!
              </h3>
              <p className="text-sm text-rose-600 dark:text-rose-400">
                Você gastou <CurrencyDisplay value={Math.abs(remainingBudget)} className="font-semibold" /> além do planejado. 
                Considere revisar seus gastos ou ajustar o planejamento.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expenses List — painel da aba ativa */}
      <TabsContent value={activeTab} className="mt-0">
      <div className="space-y-4 max-h-[calc(100vh-400px)] overflow-y-auto">
        {abaVazia && (
          <DueEmptyState
            {...ABA_VAZIA[activeTab as Exclude<DailyExpenseTab, 'all'>]}
            backLabel="Voltar para todas as despesas"
            onBack={() => setActiveTab('all')}
          />
        )}
        {semResultadoNaBusca && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Search className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Nenhuma despesa encontrada</h3>
              <p className="text-sm text-muted-foreground text-center max-w-md">
                Nenhuma despesa desta aba corresponde à busca.
              </p>
            </CardContent>
          </Card>
        )}
        {sortedExpenses.length > 0 ? (
          sortedExpenses.map((expense) => {
            const realized = Number(expense.realized_total || 0);
            const planned = Number(expense.planned_total || 0);
            const progress = planned > 0 ? (realized / planned) * 100 : 0;
            const suggestion = getSuggestion(expense);
            const isOverBudget = isDailyExpenseExceeded(expense);
            const remainingOccurrences = expense.frequency - (expense.realized_count || 0);
            const IconComponent = categoryIcons[expense.category || ''] || ShoppingCart;

            return (
              <Card 
                key={expense.id} 
                className={`transition-all ${isOverBudget ? 'border-rose-200 dark:border-rose-900' : ''}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    {/* Left: Info */}
                    <div className="flex items-start gap-4 flex-1">
                      {canPlanInPeriod && (
                        <Checkbox
                          checked={selectedExpenseIds.includes(expense.id)}
                          onCheckedChange={(checked) =>
                            handleToggleExpenseSelection(expense.id, checked === true)
                          }
                          aria-label={`Selecionar ${expense.name}`}
                          className="mt-1"
                        />
                      )}
                      <div className={`p-2.5 rounded-lg ${isOverBudget ? 'bg-rose-100 text-rose-600' : 'bg-muted'}`}>
                        <IconComponent className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-foreground">{expense.name}</h3>
                          {expense.category && (
                            <Badge variant="secondary" className="text-xs">
                              {expense.category}
                            </Badge>
                          )}
                          <Badge variant={getStatusColor(expense)} className="text-xs">
                            {expense.realized_count || 0} de {expense.frequency} vezes
                          </Badge>
                        </div>
                        
                        {/* Progress bar */}
                        <div className="space-y-1">
                          <Progress 
                            value={Math.min(100, progress)} 
                            className={`h-2 ${isOverBudget ? '[&>div]:bg-rose-500' : ''}`}
                          />
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>
                              <CurrencyDisplay value={realized} className="font-medium text-foreground" /> de{' '}
                              <CurrencyDisplay value={planned} />
                            </span>
                            <span className={isOverBudget ? 'text-rose-600 font-medium' : ''}>
                              {progress.toFixed(0)}%
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>Saldo restante:</span>
                            <CurrencyDisplay
                              value={planned - realized}
                              size="xs"
                              showSign
                              colorize
                              className="font-medium"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2 sm:flex-col sm:items-end">
                      <div className="flex items-center gap-2">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenRealizeDialog(expense)}
                                disabled={isRealizing || !canRealizeInPeriod}
                                data-tour-id={
                                  tutorialExpense?.id === expense.id
                                    ? FIRST_ACCESS_TUTORIAL_STEPS.dailyLaunch
                                    : undefined
                                }
                                className="gap-1.5"
                              >
                                <Check className="h-4 w-4" />
                                Lançar
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Registrar um gasto</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={canPlanInPeriod ? () => handleOpenEditDialog(expense) : undefined}
                              disabled={!canPlanInPeriod}
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Editar Planejado
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleOpenAnalyticsDialog(expense)}>
                              <Calculator className="h-4 w-4 mr-2" />
                              Resumo Analítico
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={canPlanInPeriod ? () => handleDeleteExpense(expense) : undefined}
                              disabled={!canPlanInPeriod}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Suggestion badge - mostra se há ocorrências restantes OU se há saldo restante */}
                      {(remainingOccurrences > 0 || suggestion > 0) && (
                        <div className="text-right">
                          <span className="text-xs text-muted-foreground">
                            {remainingOccurrences <= 0 ? 'Saldo restante:' : 'Sugestão:'}
                          </span>
                          <CurrencyDisplay 
                            value={suggestion} 
                            className={`block text-sm font-semibold ${suggestion < 0 ? 'text-rose-600' : 'text-emerald-600'}`}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Collapsible Analytics */}
                  <Collapsible open={isAnalyticsOpen[expense.id]}>
                    <CollapsibleContent className="mt-4">
                      <Separator className="mb-4" />
                      <div className="grid gap-4 sm:grid-cols-3">
                        <div className="space-y-1">
                          <span className="text-xs text-muted-foreground">Frequência planejada</span>
                          <p className="text-sm font-medium">{expense.frequency}x no período</p>
                        </div>
                        <div className="space-y-1">
                          <span className="text-xs text-muted-foreground">Valor médio planejado</span>
                          <CurrencyDisplay value={expense.average_value} className="text-sm font-medium" />
                        </div>
                        <div className="space-y-1">
                          <span className="text-xs text-muted-foreground">Valor médio realizado</span>
                          <CurrencyDisplay 
                            value={(expense.realized_count || 0) > 0 ? realized / (expense.realized_count || 1) : 0} 
                            className="text-sm font-medium" 
                          />
                        </div>
                        <div className="space-y-1">
                          <span className="text-xs text-muted-foreground">Saldo restante</span>
                          <CurrencyDisplay 
                            value={planned - realized} 
                            showSign 
                            colorize 
                            className="text-sm font-medium" 
                          />
                        </div>
                        <div className="space-y-1">
                          <span className="text-xs text-muted-foreground">Ocorrências restantes</span>
                          <p className="text-sm font-medium">{remainingOccurrences}x</p>
                        </div>
                      </div>
                      {expense.notes && (
                        <div className="mt-4">
                          <span className="text-xs text-muted-foreground">Observações</span>
                          <p className="text-sm">{expense.notes}</p>
                        </div>
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                </CardContent>
              </Card>
            );
          })
        ) : semDespesas && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <ShoppingCart className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                Nenhuma despesa diária cadastrada
              </h3>
              <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
                Comece adicionando suas despesas do dia a dia como supermercado, padaria, gasolina, etc.
                Defina a frequência e valor médio para cada uma.
              </p>
              <Button
                onClick={handleOpenCreateDialog}
                className="gap-2"
                disabled={!canPlanInPeriod}
              >
                <Plus className="h-4 w-4" />
                Adicionar Despesa
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
      </TabsContent>
      </Tabs>

      <Dialog open={isBulkDeleteDialogOpen} onOpenChange={setIsBulkDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Excluir despesas selecionadas</DialogTitle>
            <DialogDescription>
              Essa ação irá excluir todas as despesas do dia a dia cadastradas. Confirma a exclusão?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkDeleteDialogOpen(false)} disabled={isBulkDeleting}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDeleteExpenses}
              disabled={isBulkDeleting || selectedExpenseCount === 0}
            >
              {isBulkDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                'Excluir'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseCreateDialog();
            return;
          }
          setIsCreateDialogOpen(true);
        }}
      >
        <DialogContent data-tour-id="daily-create-dialog" className="w-[95vw] max-w-[750px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isCreateDialogInEditMode ? 'Editar Despesa do Dia a Dia' : 'Nova Despesa do Dia a Dia'}
            </DialogTitle>
            <DialogDescription>
              {isCreateDialogInEditMode
                ? `Edite os detalhes de "${expenseToEdit?.name || ''}".`
                : 'São as despesas que ocorrem uma ou mais vezes ao longo do mês e têm correlação com o seu dia a dia - supermercado, restaurante, cinema etc. Veja a listagem.'}
            </DialogDescription>
            <div className="flex flex-col items-start gap-3 pt-4">
              {!isCreateDialogInEditMode && (
                <>
                  {/* Cópia de despesas do dia a dia - requer copia-de-despesas-do-dia-a-dia */}
                  <FeatureGuard
                    feature="copia-de-despesas-do-dia-a-dia"
                    fallback={
                      <FeatureLockButton
                        featureName="Cópia de Despesas do Dia a Dia"
                        label="Copiar dados anteriores"
                        size="sm"
                      />
                    }
                  >
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setIsCopyPreviousDialogOpen(true)}
                      disabled={isCopying}
                      className="gap-2"
                    >
                      {isCopying ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                      Copiar dados anteriores
                    </Button>
                  </FeatureGuard>
                  <p className="text-xs text-muted-foreground text-left">
                    Se você já cadastrou despesas no período anterior, você pode copiá-las para este período.
                  </p>
                </>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsPeriodCalendarDialogOpen(true)}
                disabled={!periodCalendarData}
                className="gap-2"
              >
                <CalendarDays className="h-4 w-4" />
                Calendário
              </Button>
            </div>
          </DialogHeader>
          <div className="grid gap-4 py-4 px-2">
            <div className="grid gap-2">
              <Label htmlFor="daily-expense-name">Nome*</Label>
              <Combobox
                id="daily-expense-name"
                mode="input"
                value={createFormData.name}
                onChange={(value) => setCreateFormData({ ...createFormData, name: value })}
                portalled={false}
                options={(expenseNamesList?.items || []).map((item) => ({
                  value: item.name,
                  label: item.name,
                }))}
                placeholder="Nome"
                emptyText="Nenhum nome encontrado"
                customLabel="Usar"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="daily-expense-frequency">Frequência da despesa*</Label>
                <Input
                  id="daily-expense-frequency"
                  type="number"
                  min="1"
                  placeholder="Ex: 4 vezes"
                  value={createFormData.frequency}
                  onChange={(e) => setCreateFormData({ ...createFormData, frequency: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">Quantas vezes você faz esse gasto no período</p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="daily-expense-average">Valor médio planejado*</Label>
                <CurrencyInput
                  id="daily-expense-average"
                  value={Number.parseFloat(createFormData.average_value) || 0}
                  onChange={(value) => setCreateFormData({ ...createFormData, average_value: String(value) })}
                />
                <p className="text-xs text-muted-foreground">Valor médio de cada gasto</p>
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="daily-expense-total">Valor total a gastar</Label>
              <div
                id="daily-expense-total"
                className="flex h-10 items-center justify-end rounded-md border border-input bg-muted/30 px-3"
              >
                <CurrencyDisplay
                  value={Number.parseInt(createFormData.frequency || '0') * Number.parseFloat(createFormData.average_value || '0')}
                  className="font-semibold text-green-600"
                />
              </div>
              <p className="text-xs text-muted-foreground">Frequência x Valor médio planejado</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="daily-expense-category">Categoria*</Label>
                <Select
                  value={createFormData.category}
                  onValueChange={(value) => setCreateFormData({ ...createFormData, category: value })}
                >
                  <SelectTrigger id="daily-expense-category">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoriesList?.items?.map((item) => (
                      <SelectItem key={item.id} value={item.name}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="daily-expense-agent">Agente da despesa*</Label>
                <Select
                  value={createFormData.payer_agent_id}
                  onValueChange={(value) => setCreateFormData({ ...createFormData, payer_agent_id: value })}
                >
                  <SelectTrigger id="daily-expense-agent" disabled={!agents?.length}>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name} {agent.is_default_agent ? '(Padrão)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="daily-expense-notes">Observações</Label>
              <Textarea
                id="daily-expense-notes"
                placeholder="Observações..."
                value={createFormData.notes}
                onChange={(e) => setCreateFormData({ ...createFormData, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseCreateDialog}>
              Cancelar
            </Button>
            <Button
              onClick={handleSavePlannedExpense}
              data-tour-id={FIRST_ACCESS_TUTORIAL_STEPS.dailySave}
              // Formulário completo fica verde: sinaliza que já pode criar.
              className={isCreateFormComplete && !isSavingPlannedExpense ? CREATE_READY_BUTTON_CLASS : undefined}
              disabled={isSavingPlannedExpense || !isCreateFormComplete}
            >
              {isSavingPlannedExpense ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                isCreateDialogInEditMode ? 'Salvar Alterações' : 'Criar Despesa'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isPeriodCalendarDialogOpen} onOpenChange={setIsPeriodCalendarDialogOpen}>
        <DialogContent className="w-[95vw] max-w-[760px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Calendário do PO atual</DialogTitle>
            <DialogDescription>
              Consulte os dias do seu período orçamentário para estimar a frequência da despesa.
            </DialogDescription>
          </DialogHeader>

          {periodCalendarData ? (
            <div className="grid gap-4 py-2">
              <p className="text-sm text-muted-foreground">
                Período:{' '}
                <span className="font-medium text-foreground">
                  {format(periodCalendarData.start, 'dd/MM/yyyy')} - {format(periodCalendarData.end, 'dd/MM/yyyy')}
                </span>{' '}
                ({periodCalendarData.totalDays} dias)
              </p>
              <div className="rounded-md border">
                <Calendar
                  mode="range"
                  selected={{ from: periodCalendarData.start, to: periodCalendarData.end }}
                  month={periodCalendarData.start}
                  numberOfMonths={2}
                  locale={ptBR}
                />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {periodCalendarData.weekdayCounts.map((weekday) => (
                  <div key={weekday.label} className="rounded-md border p-2">
                    <p className="text-xs text-muted-foreground">{weekday.label}</p>
                    <p className="text-sm font-semibold">{weekday.count}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-2">
              Não foi possível carregar o período orçamentário atual.
            </p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPeriodCalendarDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Previous Daily Expenses Dialog */}
      <Dialog open={isCopyPreviousDialogOpen} onOpenChange={setIsCopyPreviousDialogOpen}>
        <DialogContent className="w-[95vw] max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Copiar dados Anteriores</DialogTitle>
            <DialogDescription>
              Escolha o período e selecione quais despesas do dia a dia deseja copiar.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Copiar dados de</Label>
                <Select
                  value={copyDataScope}
                  onValueChange={(value: 'planned' | 'realized') => setCopyDataScope(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planned">Planejado</SelectItem>
                    <SelectItem value="realized">Realizado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Período Orçamentário</Label>
                <Select value={copySourcePeriodId} onValueChange={setCopySourcePeriodId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um período" />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      if (isCopySourcesLoading) return (
                          <SelectItem value="__loading__" disabled>
                            Carregando períodos...
                          </SelectItem>
                      );
                      if ((copySourcePeriods || []).length === 0) return (
                          <SelectItem value="__empty__" disabled>
                            Nenhum período anterior com dados
                          </SelectItem>
                      );
                      return (
                          (copySourcePeriods || []).map((period) => (
                            <SelectItem key={period.id} value={period.id}>
                              {formatPeriodLabel(period.start_date, period.end_date)}
                            </SelectItem>
                          ))
                      );
                    })()}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[48px]">
                      <Checkbox
                        checked={allCopySelected ? true : someCopySelected ? 'indeterminate' : false}
                        onCheckedChange={(checked) => handleToggleCopyAll(checked === true)}
                        aria-label="Selecionar todas"
                      />
                    </TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead className="text-center">Frequência</TableHead>
                    <TableHead className="text-right">Valor Médio</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Agente</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isCopySourceExpensesLoading && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground">
                        Carregando despesas...
                      </TableCell>
                    </TableRow>
                  )}
                  {!isCopySourceExpensesLoading && !copySourcePeriodId && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground">
                        Selecione um período para listar as despesas.
                      </TableCell>
                    </TableRow>
                  )}
                  {!isCopySourceExpensesLoading && copySourcePeriodId && copyCandidates.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground">
                        Nenhuma despesa disponível para copiar.
                      </TableCell>
                    </TableRow>
                  )}
                  {!isCopySourceExpensesLoading &&
                    copySourcePeriodId &&
                    copyCandidates.map((expense) => {
                      const selected = selectedCopyIds.includes(expense.id);
                      const realizedCount = Number(expense.realized_count || 0);
                      const realizedTotal = Number(expense.realized_total || 0);
                      const averageRealized = realizedCount > 0 ? realizedTotal / realizedCount : 0;
                      const agentName =
                        expense.payer_agent?.name ||
                        (expense.payer_agent_id ? agentNameById.get(expense.payer_agent_id) : undefined) ||
                        '-';
                      const displayFrequency =
                        copyDataScope === 'realized' ? realizedCount : Number(expense.frequency || 0);
                      const displayAverage =
                        copyDataScope === 'realized' ? averageRealized : Number(expense.average_value || 0);
                      const displayTotal =
                        copyDataScope === 'realized' ? realizedTotal : Number(expense.planned_total || 0);

                      return (
                        <TableRow key={expense.id} data-state={selected ? 'selected' : undefined}>
                          <TableCell className="w-[48px]">
                            <Checkbox
                              checked={selected}
                              onCheckedChange={(checked) =>
                                handleToggleCopySelection(expense.id, checked === true)
                              }
                              aria-label={`Selecionar ${expense.name}`}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{expense.name}</TableCell>
                          <TableCell className="text-center">{displayFrequency}</TableCell>
                          <TableCell className="text-right">
                            <CurrencyDisplay value={displayAverage} />
                          </TableCell>
                          <TableCell className="text-right">
                            <CurrencyDisplay value={displayTotal} />
                          </TableCell>
                          <TableCell>{expense.category || '-'}</TableCell>
                          <TableCell>{agentName}</TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{selectedCopyCount} de {copyCandidates.length} selecionadas</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCopyPreviousDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCopySelectedExpenses}
              disabled={isCopying || !copySourcePeriodId || selectedCopyCount === 0 || !currentPeriod?.id}
            >
              {isCopying ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Copiando...
                </>
              ) : (
                'Salvar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Realize Dialog */}
      <Dialog open={isRealizeDialogOpen} onOpenChange={setIsRealizeDialogOpen}>
        <DialogContent data-tour-id="daily-realize-dialog" className="w-[95vw] max-w-[750px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Lançar Gasto</DialogTitle>
            <DialogDescription>
              Registre um gasto de "{selectedExpense?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 px-2">
            {/* Summary */}
            <Card className="bg-muted/50">
              <CardContent className="py-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Planejado:</span>
                  <CurrencyDisplay value={selectedExpense?.planned_total || 0} />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Realizado:</span>
                  <CurrencyDisplay value={selectedExpense?.realized_total || 0} />
                </div>
                <Separator />
                <div className="flex justify-between text-sm font-medium">
                  <span>Saldo restante:</span>
                  <CurrencyDisplay 
                    value={(selectedExpense?.planned_total || 0) - (selectedExpense?.realized_total || 0)} 
                    colorize 
                    showSign
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Ocorrências restantes:</span>
                  <span>{(selectedExpense?.frequency || 0) - (selectedExpense?.realized_count || 0)}x</span>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="realize-amount">Valor pago</Label>
                <CurrencyInput
                  id="realize-amount"
                  value={realizeFormData.amount || 0}
                  onChange={(value) => setRealizeFormData({ ...realizeFormData, amount: value })}
                />
                <div className="flex min-h-6 items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    Sugestão: <CurrencyDisplay value={getSuggestion(selectedExpense!)} className="font-medium" />
                  </p>
                  {selectedExpense?.realizations && selectedExpense.realizations.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-xs h-6 px-2 text-primary"
                      onClick={() => {
                        const lastRealization = selectedExpense.realizations![selectedExpense.realizations!.length - 1];
                        setRealizeFormData(prev => ({
                          ...prev,
                          amount: lastRealization.amount || prev.amount,
                          payment_method: lastRealization.payment_method || prev.payment_method,
                          notes: lastRealization.notes || prev.notes,
                        }));
                      }}
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Copiar última
                    </Button>
                  )}
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="realize-date">Data de pagamento</Label>
                <DateInput
                  id="realize-date"
                  value={realizeFormData.realization_date}
                  onChange={(value) => setRealizeFormData({ ...realizeFormData, realization_date: value })}
                  placeholder="Selecione a data"
                />
                <div className="h-6" aria-hidden="true" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
              <div className="grid gap-2">
                <Label htmlFor="realize-agent">Quem pagou</Label>
                <Select 
                  value={realizeFormData.agent_id} 
                  onValueChange={(value) => setRealizeFormData({ ...realizeFormData, agent_id: value })}
                >
                  <SelectTrigger id="realize-agent">
                    <SelectValue placeholder="Selecione o agente" />
                  </SelectTrigger>
                  <SelectContent>
                    {payerAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name} {agent.is_default_agent ? '(Padrão)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="realize-payment-method">Forma de pagamento</Label>
                <Select
                  value={realizeFormData.payment_method}
                  onValueChange={(value) => setRealizeFormData({ ...realizeFormData, payment_method: value })}
                >
                  <SelectTrigger id="realize-payment-method">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    {paymentMethodsList?.items?.map((item) => (
                      <SelectItem key={item.id} value={item.name}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="realize-notes">Observações</Label>
              <Textarea
                id="realize-notes"
                placeholder="Observações..."
                value={realizeFormData.notes || ''}
                onChange={(e) => setRealizeFormData({ ...realizeFormData, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRealizeDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleRealizeExpense}
              data-tour-id={FIRST_ACCESS_TUTORIAL_STEPS.dailyConfirm}
              disabled={isRealizing || !realizeFormData.amount || !realizeFormData.payment_method}
            >
              {isRealizing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Lançando...
                </>
              ) : (
                'Lançar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={isAnalyticsDialogOpen} onOpenChange={setIsAnalyticsDialogOpen}>
        <DialogContent className="w-[95vw] max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Resumo Analítico - {analyticsExpense?.name || ''}</DialogTitle>
            <DialogDescription>
              Informações detalhadas sobre os lançamentos e a comparação Planejado x Realizado.
            </DialogDescription>
          </DialogHeader>

          {analyticsExpense && plannedVsRealized && (
            <div className="grid gap-6 py-2">
              {/* Detalhamento dos Pagamentos */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-center text-primary">
                  Detalhamento dos Pagamentos
                </h3>
                <div className="rounded-lg border bg-card">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/30">
                        <tr>
                          <th className="text-left font-medium px-4 py-3">Data</th>
                          <th className="text-left font-medium px-4 py-3">Quem pagou</th>
                          <th className="text-left font-medium px-4 py-3">Forma de Pagamento</th>
                          <th className="text-right font-medium px-4 py-3">Valor</th>
                          <th className="text-right font-medium px-4 py-3">Ação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(analyticsExpense.realizations || []).length > 0 ? (
                          [...(analyticsExpense.realizations || [])]
                            .sort((a, b) => b.realization_date.localeCompare(a.realization_date))
                            .map((r) => (
                              <tr key={r.id} className="border-t">
                                <td className="px-4 py-3">
                                  {formatDatePtBR(r.realization_date) ?? r.realization_date}
                                </td>
                                <td className="px-4 py-3">
                                  {r.agent?.name ||
                                    (r.agent_id ? agentNameById.get(r.agent_id) : undefined) ||
                                    '-'}
                                </td>
                                <td className="px-4 py-3">{r.payment_method || '-'}</td>
                                <td className="px-4 py-3 text-right">
                                  <CurrencyDisplay value={Number(r.amount) || 0} />
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    aria-label={`Editar lançamento - ${analyticsExpense.name}`}
                                    onClick={() => handleOpenEditRealizationDialog(analyticsExpense, r)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    aria-label={`Excluir lançamento - ${analyticsExpense.name}`}
                                    onClick={() => handleOpenDeleteRealizationDialog(analyticsExpense, r)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </td>
                              </tr>
                            ))
                        ) : (
                          <tr>
                            <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                              Nenhum lançamento registrado neste período.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="flex justify-end">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleAddRealizationFromAnalytics}
                          disabled={isRealizing || !canRealizeInPeriod}
                          className="gap-1.5"
                        >
                          <Plus className="h-4 w-4" />
                          Lançar
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Realizar mais um lançamento para está despesa</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>

              {/* Sugestão para o próximo gasto */}
              {(() => {
                const suggestionValue = getSuggestion(analyticsExpense);
                const isNegative = suggestionValue <= 0;
                return (
                  <div className={`rounded-xl p-4 flex items-center justify-between ${
                    isNegative
                      ? 'bg-rose-500/15 border border-rose-200 dark:border-rose-900'
                      : 'bg-emerald-500/15 border border-emerald-200 dark:border-emerald-900'
                  }`}>
                    <span className={`text-lg font-semibold ${
                      isNegative
                        ? 'text-rose-700 dark:text-rose-300'
                        : 'text-emerald-700 dark:text-emerald-300'
                    }`}>
                      Sugestão para o próximo Gasto:
                    </span>
                    <span className={`rounded-lg bg-white/80 dark:bg-foreground/10 px-4 py-2 text-xl font-bold ${
                      isNegative
                        ? 'text-rose-700 dark:text-rose-300'
                        : 'text-emerald-700 dark:text-emerald-300'
                    }`}>
                      <CurrencyDisplay value={Math.max(0, suggestionValue)} />
                    </span>
                  </div>
                );
              })()}

              {/* Comparação Planejado x Realizado */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-center text-primary">
                  Comparação Planejado x Realizado
                </h3>
                <div className="rounded-lg border bg-card">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/30">
                        <tr>
                          <th className="text-left font-medium px-4 py-3">&nbsp;</th>
                          <th className="text-left font-medium px-4 py-3">Valor Médio</th>
                          <th className="text-center font-medium px-4 py-3">Frequência</th>
                          <th className="text-right font-medium px-4 py-3">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t">
                          <td className="px-4 py-3 font-medium text-amber-600">P</td>
                          <td className="px-4 py-3">
                            <CurrencyDisplay value={plannedVsRealized.plannedAverage} />
                          </td>
                          <td className="px-4 py-3 text-center">{plannedVsRealized.plannedFrequency}</td>
                          <td className="px-4 py-3 text-right">
                            <CurrencyDisplay value={plannedVsRealized.plannedTotal} />
                          </td>
                        </tr>
                        <tr className="border-t">
                          <td className="px-4 py-3 font-medium text-muted-foreground">R</td>
                          <td className="px-4 py-3">
                            <CurrencyDisplay value={plannedVsRealized.realizedAverage} />
                          </td>
                          <td className="px-4 py-3 text-center">{plannedVsRealized.realizedCount}</td>
                          <td className="px-4 py-3 text-right">
                            <CurrencyDisplay value={plannedVsRealized.realizedTotal} />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-end gap-3 px-4 py-4 border-t">
                    <span className="text-sm font-medium text-muted-foreground">Saldo:</span>
                    <span
                      className={`rounded-lg px-3 py-1 text-sm font-bold ${
                        plannedVsRealized.saldo <= 0
                          ? 'bg-rose-500/15 text-rose-700 dark:text-rose-300'
                          : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                      }`}
                    >
                      {plannedVsRealized.saldoDisplay}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Realization Dialog */}
      <Dialog open={isEditRealizationDialogOpen} onOpenChange={setIsEditRealizationDialogOpen}>
        <DialogContent className="w-[95vw] max-w-[750px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar – {analyticsExpense?.name || ''}</DialogTitle>
            <DialogDescription>Edite os dados do lançamento selecionado.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 px-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-realization-amount">Valor pago</Label>
                <CurrencyInput
                  id="edit-realization-amount"
                  value={editRealizationFormData.amount || 0}
                  onChange={(value) => setEditRealizationFormData({ ...editRealizationFormData, amount: value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-realization-date">Data de pagamento</Label>
                <DateInput
                  id="edit-realization-date"
                  value={editRealizationFormData.realization_date}
                  onChange={(value) =>
                    setEditRealizationFormData({ ...editRealizationFormData, realization_date: value })
                  }
                  placeholder="Selecione a data"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-realization-agent">Quem pagou</Label>
                <Select
                  value={editRealizationFormData.agent_id}
                  onValueChange={(value) => setEditRealizationFormData({ ...editRealizationFormData, agent_id: value })}
                >
                  <SelectTrigger id="edit-realization-agent">
                    <SelectValue placeholder="Selecione o agente" />
                  </SelectTrigger>
                  <SelectContent>
                    {payerAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name} {agent.is_default_agent ? '(Padrão)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-realization-payment-method">Forma de pagamento</Label>
                <Select
                  value={editRealizationFormData.payment_method}
                  onValueChange={(value) =>
                    setEditRealizationFormData({ ...editRealizationFormData, payment_method: value })
                  }
                >
                  <SelectTrigger id="edit-realization-payment-method">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    {paymentMethodsList?.items?.map((item) => (
                      <SelectItem key={item.id} value={item.name}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-realization-notes">Observações</Label>
              <Textarea
                id="edit-realization-notes"
                placeholder="Observações..."
                value={editRealizationFormData.notes || ''}
                onChange={(e) => setEditRealizationFormData({ ...editRealizationFormData, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditRealizationDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleUpdateRealization}
              disabled={isUpdatingRealization || !editRealizationFormData.amount || !editRealizationFormData.payment_method}
            >
              {isUpdatingRealization ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Realization Dialog */}
      <Dialog open={isDeleteRealizationDialogOpen} onOpenChange={setIsDeleteRealizationDialogOpen}>
        <DialogContent className="w-[95vw] max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Excluir – {analyticsExpense?.name || ''}</DialogTitle>
            <DialogDescription>Tem certeza de que deseja excluir este lançamento?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteRealizationDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeleteRealization} disabled={isDeletingRealization}>
              {isDeletingRealization ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                'Excluir'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
